import axios from 'axios'
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { ElMessage } from 'element-plus'
import router from '@/router'
import { useAuthStore } from '@/stores/auth'

declare module 'axios' {
  export interface AxiosRequestConfig {
    silent?: boolean
  }
}

const service: AxiosInstance = axios.create({
  baseURL: '/api',
  // 5 minutes for long-running backend operations
  timeout: 5 * 60 * 1000,
  withCredentials: true
})

const readCookie = (name: string) => {
  const item = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`))
  return item ? decodeURIComponent(item.split('=')[1]) : ''
}

let redirecting401 = false

const redirectToLoginAfterSessionExpired = async () => {
  const currentRoute = router.currentRoute.value
  if (currentRoute.path === '/login') return

  const auth = useAuthStore()
  auth.user = null
  auth.initialized = true

  ElMessage.error({
    message: '登录会话已过期或已在其他位置失效。请重新登录后继续操作，系统会尽量返回到刚才的页面。',
    duration: 10000,
    showClose: true
  })

  await router.replace({
    path: '/login',
    query: { redirect: currentRoute.fullPath }
  })
}

const attachInterceptors = (client: AxiosInstance) => {
  client.interceptors.request.use((config) => {
    const method = (config.method || 'get').toUpperCase()
    if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
      const csrfToken = readCookie('ODS_CSRF')
      if (csrfToken) config.headers['X-CSRF-Token'] = csrfToken
    }
    return config
  })

  client.interceptors.response.use(
    (response: AxiosResponse) => {
      const res = response.data
      if (res.code && res.code !== 'ODS-0000') {
        ElMessage.error(res.message || 'Error')
        return Promise.reject(new Error(res.message || 'Error'))
      }
      return res.data
    },
    (error) => {
      if (axios.isCancel(error) || error.code === 'ERR_CANCELED') {
        return Promise.reject(error)
      }
      const status = error.response?.status
      const url = error.config?.url ?? ''
      if (error.config?.silent) {
        // Some background status calls should fail quietly.
      } else if (status === 401) {
        if (url.includes('/auth/login')) {
          // let LoginView handle this
        } else if (!redirecting401) {
          redirecting401 = true
          redirectToLoginAfterSessionExpired()
            .finally(() => { redirecting401 = false })
        }
      } else if (status === 403) {
        ElMessage.error('无权限执行该操作')
      } else {
        ElMessage.error(error.response?.data?.message || error.message || 'Request Error')
      }
      return Promise.reject(error)
    }
  )
}

attachInterceptors(service)

export const apiService = service

// Typed wrappers — interceptor unwraps `res.data`, so callers receive `Promise<T>` directly.
// Uses Axios's dual generic `Method<T, R = AxiosResponse<T>>` with R = T to express the unwrapped shape.
export const apiGet = <T = unknown>(url: string, cfg?: AxiosRequestConfig) =>
  service.get<T, T>(url, cfg)
export const apiPost = <T = unknown>(url: string, data?: unknown, cfg?: AxiosRequestConfig) =>
  service.post<T, T>(url, data, cfg)
export const apiPatch = <T = unknown>(url: string, data?: unknown, cfg?: AxiosRequestConfig) =>
  service.patch<T, T>(url, data, cfg)
export const apiDelete = <T = unknown>(url: string, cfg?: AxiosRequestConfig) =>
  service.delete<T, T>(url, cfg)

export default service
